/**
 * useTutorLiveRoom — la salle du tuteur (`TutorLiveRoomContext`) : le moteur (`useTutorLive`, sur la
 * salle commune `useLiveRoom`), traduit pour `<TutorLivePage>` par la vue (`tutorLiveRoomViewModel`) et
 * l'adaptateur (`tutorLiveRoomAdapter`).
 *
 * Ce qu'il rend, et d'où l'ancien le tenait :
 *  - `notice` à la place de la salle (`TutorLiveScreen.js:306-381`) ; « Réessayer » et « Annuler »
 *    (`ConnectingView.js:48-66`, leurs mesures `textbutton.clicked.retry_connecting` / `cancel_connecting`) ;
 *  - `me`, les autres tuteurs de la salle (`coTutors`, une tuile chacun, `TutorRoomView.js`), `roomKind`, `startedAt`, le prénom du champion (`helperName`) ;
 *  - `renderVideo(id)` : les vraies pistes d'un participant, caméra ET écran partagé côte à côte, de
 *    chacun de ses appareils (`TutorRoomView.js:62-72`, `LiveParticipant.js:457-476`) ;
 *  - `layer` : une boîte à la fois, la plus forte de la salle — la sortie forcée, l'autre onglet
 *    (`KeepTabModal`, sans fermeture au voile), la notification d'une autre séance, le live suivant
 *    (`TutorOverrunDialog`, la croix acquitte aussi), les permissions refusées (web) ;
 *  - `mounts` : CallKeep (`LiveContainerV3.js:123`) ; `chatRequest` : une notification de message de la
 *    séance touchée ouvre le chat sur son fil ;
 *  - le jingle d'un résumé du champion terminé (`LiveParticipant.js:284-299`), quand l'écouteur global ne l'a
 *    pas déjà joué (`useLiveKitEventListener.js:398-405` sonne chaque `agentConversationCompleted` à résumé) :
 *    un résumé découvert par la relecture de la séance, ou déjà dans la file à l'entrée dans la salle.
 *
 * Les sons d'arrivée et de départ d'un élève, d'une main levée, restent à `LKEvents` (global) : rien
 * n'est doublé ici.
 */
import { useTracks } from "@livekit/components-react"
import { useNavigation } from "@react-navigation/native"
import { RoomEvent, Track } from "livekit-client"
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { StyleSheet, View } from "react-native"
import { useSelector } from "react-redux"

import { livePeople } from "../../../../../../engine/live/room/livePeople"
import CallKeepManagerM from "../../../../../../engine/live/room/callKeep/CallKeepManagerM"
import { playLiveSound } from "../../../../../../engine/live/student/playLiveSound"
import {
    browserOf,
    summariesNotAnnounced,
    summariesToChime,
    tracksOfParticipant,
    type CompletedConversationLike,
} from "../../../../../../engine/live/tutor/tutorRoomRules"
import { useTutorLive } from "../../../../../../engine/live/tutor/useTutorLive"
import { usePermissionPrompt } from "../../../../../../engine/precall/permissionPrompt"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import {
    toExitDialog,
    toOverrunDialog,
    toPermissionsDialog,
    toTapDialog,
    toTutorLiveCoTutors,
    toTutorLiveMe,
    toTutorRoomKind,
    type ApiTutorCastSession,
    LIVE_HELPER_SLUG,
    tutorHelperName,
    tutorLiveStartedAt,
} from "../../../../adapters/tutor/Live/tutorLiveRoomAdapter"
import {
    isTutorRoomDialogAutomatic,
    pickTutorRoomDialog,
    tutorLiveRoomNotice,
    type TutorRoomDialogKey,
} from "../../../../view-models/tutor/Live/tutorLiveRoomViewModel"
import { track } from "../../../hooks/trackEvent"
import { LiveVideo } from "../../student/Live/LiveVideo"
import { type TutorLiveLayer, type TutorLiveRoomContext } from "./tutorLiveContract"

/** Une piste coupée ou rouverte relit les pistes ; pas la voix (`liveTracks.TRACK_EVENTS`). */
const TRACK_EVENTS: RoomEvent[] = [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted]

/* eslint-disable @typescript-eslint/no-require-imports */
/** Un point d'avancement du champion vient de se terminer (`LiveParticipant.js:294`). */
const SUMMARY_SOUND = require("../../../../../../../assets/sounds/jingles_STEEL16.mp3") as number
/* eslint-enable @typescript-eslint/no-require-imports */

export const TUTOR_ROOM_TRACKING = {
    retry: "textbutton.clicked.retry_connecting",
    cancel: "textbutton.clicked.cancel_connecting",
} as const

type RootState = { liveMode?: { completedConversations?: CompletedConversationLike[] | null } }

export function useTutorLiveRoom(): TutorLiveRoomContext {
    const live = useTutorLive()
    const { room, user, liveSession, actions, telemetry } = live
    const navigation = useNavigation() as unknown as { push: (route: string, params?: object) => void }
    const [enteredAt] = useState(() => Date.now())

    /* ── moi ── */
    const local = room.participants.find((participant) => participant.isLocal) ?? null
    const localCamera = local?.cameraOn === true
    const localMic = local?.micOn === true
    const me = useMemo(
        () => toTutorLiveMe(user, local != null ? { cameraOn: localCamera, micOn: localMic } : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user?.id, user?.firstname, user?.display_name, user?.avatar, local != null, localCamera, localMic]
    )

    /* ── les autres tuteurs de la salle ── */
    const coTutors = useMemo(
        () => toTutorLiveCoTutors(liveSession as ApiTutorCastSession | null, livePeople(room.participants), me.id),
        [liveSession, room.participants, me.id]
    )

    /* ── les pistes ── */
    const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { updateOnlyOn: TRACK_EVENTS })
    const entries = useMemo(
        () =>
            tracks.map((trackRef) => ({
                trackRef,
                source: String(trackRef.source),
                identity: trackRef.participant?.identity,
                isLocal: trackRef.participant?.isLocal === true,
                muted: trackRef.participant == null || trackRef.publication == null || trackRef.publication.isMuted,
            })),
        [tracks]
    )
    const renderVideo = useCallback(
        (participantId: string) => {
            const own = tracksOfParticipant(entries, participantId)
            if (own.length === 0) return null
            const views = own.map((entry, index) =>
                createElement(
                    View,
                    { key: `${entry.identity ?? ""}:${entry.source}:${index}`, style: styles.cell },
                    createElement(LiveVideo, {
                        track: entry.trackRef,
                        // Ma caméra en miroir ; chaque image entière (`contain`) : l'écran partagé et la caméra
                        // d'un élève comme l'ancienne tuile (S-95, « contenue »), et la mienne aussi, que la
                        // vignette coupait (Wajih, live en prod du 03/11 : « plus aucune image coupée »).
                        source:
                            entry.source === Track.Source.ScreenShare
                                ? "screen"
                                : entry.isLocal
                                  ? "me"
                                  : (`round:${participantId}` as const),
                        fit: "contain",
                    })
                )
            )
            return createElement(View, { style: styles.row }, ...views)
        },
        [entries]
    )

    /* ── le jingle du résumé du champion ── */
    const completed = useSelector((state: RootState) => state.liveMode?.completedConversations ?? null)
    const memberIds = useMemo(
        () =>
            (liveSession?.users ?? [])
                .map((member) => (member?.id != null ? String(member.id) : null))
                .filter((id): id is string => id != null && id !== me.id),
        [liveSession?.users, me.id]
    )
    const chimedRef = useRef<Set<string>>(new Set())
    const summariesAnnounced = live.summariesAnnounced
    useEffect(() => {
        const fresh = summariesToChime(completed, memberIds, chimedRef.current)
        if (fresh.length === 0) return
        for (const id of fresh) chimedRef.current.add(id)
        // L'écouteur global a déjà sonné celles qu'un `agentConversationCompleted` a apportées : un jingle, pas deux.
        if (summariesNotAnnounced(fresh, summariesAnnounced).length > 0) playLiveSound(SUMMARY_SOUND, telemetry)
    }, [completed, memberIds, summariesAnnounced, telemetry])

    /* ── les notifications touchées ── */
    const [chatRequest, setChatRequest] = useState<TutorLiveRoomContext["chatRequest"]>(null)
    const tap = live.tap
    const dismissTap = actions.dismissTap
    useEffect(() => {
        if (tap == null) return
        if (tap.outcome.kind === "chat") {
            setChatRequest({ channel: tap.outcome.channel, seq: tap.seq })
            dismissTap()
        } else if (tap.outcome.kind === "push") {
            navigation.push(tap.outcome.route, tap.outcome.params)
            dismissTap()
        }
        // Une fois par toucher (`seq`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tap?.seq])

    /* ── les boîtes ── */
    const [permissionsDismissed, setPermissionsDismissed] = useState(false)
    const exitDialog = live.exit != null ? toExitDialog(live.exit) : null
    const tapDialog = toTapDialog(tap?.outcome)
    const overrunDialog = toOverrunDialog(live.overrun)
    const permissionsDialog =
        IS_WEB && live.media.checked && live.media.granted === false && !permissionsDismissed
            ? toPermissionsDialog(browserOf(typeof navigator !== "undefined" ? navigator.userAgent : null))
            : null
    const picked = pickTutorRoomDialog({
        exit: exitDialog,
        keep_tab: room.tabs.showKeepTabModal ? { kind: "keep_tab" } : null,
        tap: tapDialog,
        overrun: overrunDialog,
        permissions: permissionsDialog,
    })
    const handlers = useMemo((): Record<
        TutorRoomDialogKey,
        Pick<NonNullable<TutorLiveLayer["dialog"]>, "onConfirm" | "onCancel" | "onDismiss">
    > => {
        const dismissPermissions = () => setPermissionsDismissed(true)
        return {
            exit: { onConfirm: actions.confirmExit, onDismiss: actions.confirmExit },
            // L'ancienne modale ne se fermait pas : toucher le voile ne garde pas cet onglet.
            keep_tab: { onConfirm: room.tabs.keepThisTab, onDismiss: () => {} },
            tap: { onConfirm: actions.dismissTap, onDismiss: actions.dismissTap },
            overrun: { onConfirm: actions.ackOverrun, onDismiss: actions.ackOverrun },
            permissions: {
                onConfirm: () => {
                    if (typeof window !== "undefined") window.location.reload()
                },
                onCancel: dismissPermissions,
                onDismiss: dismissPermissions,
            },
        }
    }, [actions, room.tabs.keepThisTab])
    const layer: TutorLiveLayer = {
        dialog: picked != null ? { value: picked.value, ...handlers[picked.key] } : null,
        dialogPriority: picked?.priority,
        // Le live suivant (les prénoms de l'autre séance) et la notification d'une autre séance s'ouvrent sans
        // geste dans la salle : ils attendent la fin d'une présentation, recadrée ou non (`pickLayer`). La sortie
        // forcée, l'autre onglet et les permissions restent montrés (`TUTOR_ROOM_AUTOMATIC_DIALOGS`).
        ...(picked != null && isTutorRoomDialogAutomatic(picked.key) ? { dialogAutomatic: true } : {}),
    }

    /* ── la place de la salle ── */
    const promptShown = usePermissionPrompt() != null
    const notice = tutorLiveRoomNotice({
        sessionKnown: liveSession != null,
        connection: live.connection,
        mediaChecked: live.media.checked,
        // La question du navigateur restée à l'écran (la règle de l'élève : rien quand l'accès est déjà acquis).
        mediaWaiting: promptShown,
        enrolledCount: live.enrolledIds.length,
        presentCount: live.presentIds.length,
        noStudentYetDue: live.noStudentYetDue,
    })
    const retry = actions.retryConnection
    const onRetryConnection = useMemo(
        () =>
            retry != null
                ? () => {
                      track({ name: TUTOR_ROOM_TRACKING.retry })
                      retry()
                  }
                : undefined,
        [retry]
    )
    const cancelConnection = actions.cancelConnection
    const onCancelConnection = useCallback(() => {
        track({ name: TUTOR_ROOM_TRACKING.cancel })
        cancelConnection()
    }, [cancelConnection])

    const mounts = useMemo(() => createElement(CallKeepManagerM), [])

    return {
        liveSessionId: live.liveSessionId,
        live: room,
        connected: live.connected,
        me,
        coTutors,
        roomKind: toTutorRoomKind(liveSession?.live_room?.type),
        startedAt: tutorLiveStartedAt(liveSession, enteredAt),
        notice,
        // « Réessayer » remplace « Annuler » (`ConnectingView.js`) : jamais les deux.
        onRetryConnection,
        onCancelConnection: onRetryConnection == null ? onCancelConnection : undefined,
        renderVideo,
        helperName: tutorHelperName(),
        helper: LIVE_HELPER_SLUG,
        telemetry,
        layer,
        mounts,
        chatRequest,
    }
}

const styles = StyleSheet.create({
    row: { flex: 1, flexDirection: "row" },
    cell: { flex: 1 },
})
